import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Typography,
  Box,
  Divider,
  Alert,
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import SubtitlesIcon from '@mui/icons-material/Subtitles';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import { useMeetingStore } from '../../contexts/meetingStore';
import { useAuthStore } from '../../contexts/authStore';
import { emitAsync } from '../../services/socket';
import * as api from '../../services/api';

const LANGUAGES = [
  { code: 'en', name: 'English' },
  { code: 'it', name: 'Italian' },
  { code: 'es', name: 'Spanish' },
  { code: 'fr', name: 'French' },
  { code: 'de', name: 'German' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'zh', name: 'Chinese' },
  { code: 'ja', name: 'Japanese' },
  { code: 'ko', name: 'Korean' },
  { code: 'ar', name: 'Arabic' },
  { code: 'ru', name: 'Russian' },
];

export default function SettingsDialog({ open, onClose }) {
  const {
    translationEnabled,
    sourceLanguage,
    targetLanguage,
    subtitlesEnabled,
    setTranslationEnabled,
    setSourceLanguage,
    setTargetLanguage,
    setSubtitlesEnabled,
    meeting,
    setMeeting,
    isHost,
  } = useMeetingStore();

  const { user, token } = useAuthStore();

  const [localTranslation, setLocalTranslation] = useState(translationEnabled);
  const [localSourceLang, setLocalSourceLang] = useState(sourceLanguage || user?.preferredLanguage || 'en');
  const [localTargetLang, setLocalTargetLang] = useState(targetLanguage);
  const [localSubtitles, setLocalSubtitles] = useState(subtitlesEnabled);
  const [localWaitingRoom, setLocalWaitingRoom] = useState(meeting?.settings?.waitingRoomEnabled || false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      setLocalTranslation(translationEnabled);
      setLocalSourceLang(sourceLanguage || user?.preferredLanguage || 'en');
      setLocalTargetLang(targetLanguage);
      setLocalSubtitles(subtitlesEnabled);
      setLocalWaitingRoom(meeting?.settings?.waitingRoomEnabled || false);
    }
  }, [open, translationEnabled, sourceLanguage, targetLanguage, subtitlesEnabled, user, meeting]);

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const languagesChanged = localSourceLang !== sourceLanguage || localTargetLang !== targetLanguage;

      // Handle translation toggle
      if (localTranslation !== translationEnabled || (localTranslation && languagesChanged)) {
        if (localTranslation) {
          // Disable first if already enabled (to restart with new settings)
          if (translationEnabled) {
            await emitAsync('disable-translation');
          }
          await emitAsync('enable-translation', {
            sourceLanguage: localSourceLang,
            targetLanguage: localTargetLang
          });
        } else {
          await emitAsync('disable-translation');
        }
        setTranslationEnabled(localTranslation);
      }

      setSourceLanguage(localSourceLang);
      setTargetLanguage(localTargetLang);
      setSubtitlesEnabled(localSubtitles);

      // Save waiting room setting via API (host only)
      console.log('[Settings] isHost:', isHost, 'meeting:', meeting, 'localWaitingRoom:', localWaitingRoom);
      if (isHost && localWaitingRoom !== (meeting?.settings?.waitingRoomEnabled || false)) {
        try {
          console.log('[Settings] Calling API to update waiting room:', meeting?.code);
          const response = await api.updateMeetingSettings(meeting.code, { waitingRoomEnabled: localWaitingRoom }, token);
          console.log('[Settings] API response:', response);
          // Update meeting in store with new settings
          setMeeting({
            ...meeting,
            settings: response.settings,
          });
        } catch (apiErr) {
          console.error('Failed to update waiting room setting:', apiErr);
          // Don't block close, just log the error
        }
      }

      onClose();
    } catch (err) {
      setError(err.message);
    }

    setSaving(false);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Meeting Settings</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Translation settings */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <TranslateIcon color="primary" />
            <Typography variant="h6">Real-time Translation</Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={localTranslation}
                onChange={(e) => setLocalTranslation(e.target.checked)}
              />
            }
            label="Enable translation"
          />

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, ml: 6 }}>
            When enabled, you'll hear translated audio from other participants
            instead of their original voices.
          </Typography>

          {localTranslation && (
            <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel>I speak</InputLabel>
                <Select
                  value={localSourceLang}
                  onChange={(e) => setLocalSourceLang(e.target.value)}
                  label="I speak"
                >
                  {LANGUAGES.map((lang) => (
                    <MenuItem key={lang.code} value={lang.code}>
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth>
                <InputLabel>Translate to</InputLabel>
                <Select
                  value={localTargetLang}
                  onChange={(e) => setLocalTargetLang(e.target.value)}
                  label="Translate to"
                >
                  {LANGUAGES.map((lang) => (
                    <MenuItem
                      key={lang.code}
                      value={lang.code}
                      disabled={lang.code === localSourceLang}
                    >
                      {lang.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          )}

          <Alert severity="info" sx={{ mt: 2 }}>
            Your speech ({LANGUAGES.find(l => l.code === localSourceLang)?.name || 'Unknown'}) will be translated to {LANGUAGES.find(l => l.code === localTargetLang)?.name || 'Unknown'} for other participants. The translated voice will match your gender setting.
          </Alert>
        </Box>

        <Divider sx={{ my: 2 }} />

        {/* Subtitles settings */}
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <SubtitlesIcon color="primary" />
            <Typography variant="h6">Subtitles</Typography>
          </Box>

          <FormControlLabel
            control={
              <Switch
                checked={localSubtitles}
                onChange={(e) => setLocalSubtitles(e.target.checked)}
              />
            }
            label="Show live subtitles"
          />

          <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
            Display real-time transcription of what's being said in the meeting.
          </Typography>
        </Box>

        {/* Waiting Room settings - Host only */}
        {isHost && (
          <>
            <Divider sx={{ my: 2 }} />

            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MeetingRoomIcon color="primary" />
                <Typography variant="h6">Waiting Room</Typography>
              </Box>

              <FormControlLabel
                control={
                  <Switch
                    checked={localWaitingRoom}
                    onChange={(e) => setLocalWaitingRoom(e.target.checked)}
                  />
                }
                label="Enable waiting room"
              />

              <Typography variant="body2" color="text.secondary" sx={{ ml: 6 }}>
                When enabled, new participants must be admitted by the host before joining the meeting.
              </Typography>

              {localWaitingRoom && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  Participants will wait in a lobby until you admit them. You'll see them in the Participants panel.
                </Alert>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
